import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { UserCircle2, AlertTriangle, BookOpen, CalendarDays, Building2, Trash2, Plus, Save } from "lucide-react";
import { StepBadge, DetailRow, fileHref } from "./common";

const EMPTY_BRANCH = { name: "", address: "", city: "", province: "", phone: "", email: "" };

const resetSummary = (r) => {
  if (!r) return "Never sent";
  const sent = `Sent ${new Date(r.sent_at).toLocaleString()}${r.sent_by_admin ? " by admin" : " (user request)"}`;
  const status = r.used
    ? `Used${r.used_at ? ` ${new Date(r.used_at).toLocaleString()}` : ""}`
    : r.expired ? "Not used — link expired" : "Not used yet — link still active";
  return `${sent} · ${status}`;
};


// Comprehensive user detail dialog — shows every piece of context an admin
// might need before approving/rejecting/revoking: avatar, step badge, other
// roles under the same email, raw profile fields, photos, parent family info,
// linked second-parent (if any), vendor profile, event-creator / marketplace
// profile, hosted events, listings, and sign-up error log.
// Admin actions bubble up to the parent: grant / revoke free access.
export default function UserDetailDialog({
  detailUser,
  detailData,
  detailLoading,
  setDetailUser,
  setDetailData,
  toggleFreeAccess,
}) {
  return (
    <Dialog open={!!detailUser} onOpenChange={(o) => { if (!o) { setDetailUser(null); setDetailData(null); } }}>
      <DialogContent className="rounded-3xl bg-cream max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="user-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest flex items-center gap-2">
            <UserCircle2 className="w-6 h-6 text-sage-dark" />
            <span data-testid="user-detail-name">{detailData?.user?.name || detailUser?.name || "User"}</span>
          </DialogTitle>
          <DialogDescription className="text-forest/60" data-testid="user-detail-email">
            {detailData?.user?.email || detailUser?.email}
          </DialogDescription>
        </DialogHeader>
        {detailLoading || !detailData ? (
          <div className="py-10 text-center text-sm text-forest/50" data-testid="user-detail-loading">Loading user detail...</div>
        ) : (
          <Body d={detailData} setDetailData={setDetailData} toggleFreeAccess={toggleFreeAccess} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function Body({ d, setDetailData, toggleFreeAccess }) {
  const u = d.user || {};
  const vp = d.vendor_profile || null;
  const rp = d.role_profile || null;
  const pp = d.parent_profile || null;
  const partner = d.partner || null;
  const linkedFrom = d.linked_from || null;
  const eventsCreated = d.events_created || [];
  const listings = d.listings || [];

  const avatarSrc = (u.photo_status === "approved" && u.photo_path)
    ? fileHref(u.photo_path)
    : (u.pending_photo_path ? fileHref(u.pending_photo_path) : "/logo.png");

  return (
    <div className="space-y-6">
      <section className="flex items-center gap-4" data-testid="user-detail-header">
        <img src={avatarSrc} alt={u.name || "User"}
          className="w-20 h-20 rounded-full object-cover border border-sage/30 bg-white shrink-0"
          data-testid="user-detail-avatar" />
        <div className="min-w-0 flex-1">
          <p className="font-heading font-bold text-forest text-lg truncate">{u.name}</p>
          <p className="text-xs text-forest/60 truncate">{u.email}</p>
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <span className="text-[10px] font-bold uppercase tracking-wide bg-sage-light text-sage-dark px-2 py-0.5 rounded-full">{u.role_label || u.role}</span>
            <span className="text-[10px] text-forest/50">
              Photo:
              <span className="ml-1 font-bold uppercase" data-testid="user-detail-photo-status">
                {u.photo_status || "none"}
              </span>
            </span>
            {u.free_access && (
              <span className="text-[10px] font-bold uppercase tracking-wide bg-amber-200/80 text-amber-900 px-2 py-0.5 rounded-full" data-testid="user-detail-free-access-badge">
                Free access
              </span>
            )}
          </div>
        </div>
        {u.role !== "admin" && (
          u.free_access ? (
            <Button size="sm" variant="outline"
              className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light shrink-0"
              onClick={() => toggleFreeAccess(u.user_id, "revoke")}
              data-testid="revoke-free-access-btn">
              Revoke free access
            </Button>
          ) : (
            <Button size="sm"
              className="rounded-full bg-sage hover:bg-sage-dark text-white shrink-0"
              onClick={() => toggleFreeAccess(u.user_id, "grant")}
              data-testid="grant-free-access-btn">
              Grant free access
            </Button>
          )
        )}
      </section>

      <section data-testid="user-detail-step-section">
        <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Current sign-up step</h4>
        <div className="flex flex-wrap items-center gap-2">
          <StepBadge step={u.onboarding_step || "Unknown"} testid="user-detail-step" />
          <span className="text-xs text-forest/60">for <strong>{u.role_label || u.role}</strong> role</span>
        </div>
        {u.rejection_reason && (
          <p className="mt-2 text-xs text-terracotta-dark bg-terracotta-light/60 rounded-xl p-2" data-testid="user-detail-rejection-reason">
            <strong>Rejection reason:</strong> {u.rejection_reason}
          </p>
        )}
      </section>

      {d.roles && d.roles.length > 1 && (
        <section data-testid="user-detail-roles-section">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Other roles under this email</h4>
          <div className="space-y-2">
            {d.roles.filter((r) => r.user_id !== u.user_id).map((r) => (
              <div key={r.user_id} className="flex flex-wrap items-center gap-2 bg-white/70 border border-sage/20 rounded-xl px-3 py-2" data-testid={`user-detail-role-${r.role}`}>
                <span className="text-sm font-bold text-forest">{r.role_label}</span>
                <StepBadge step={r.onboarding_step} testid={`user-detail-role-step-${r.role}`} />
                <span className="ml-auto text-xs text-forest/50">Sub: {r.subscription_status}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="user-detail-profile-section">
        <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Profile submissions</h4>
        <div className="bg-white/80 border border-sage/20 rounded-2xl p-4">
          <DetailRow label="Name" value={u.name} testid="user-detail-field-name" />
          <DetailRow label="First name" value={u.first_name} testid="user-detail-field-first-name" />
          <DetailRow label="Surname" value={u.surname} testid="user-detail-field-surname" />
          <DetailRow label="Email verified" value={u.email_verified ? "Yes" : "No"} testid="user-detail-field-email-verified" />
          <DetailRow label="Profile completed" value={u.profile_completed ? "Yes" : "No"} testid="user-detail-field-profile-completed" />
          <DetailRow label="Vendor status" value={u.vendor_status} testid="user-detail-field-vendor-status" />
          <DetailRow label="Subscription" value={u.subscription_status} testid="user-detail-field-subscription" />
          <DetailRow label="Free access" value={u.free_access ? `Granted ${u.free_access_granted_at ? new Date(u.free_access_granted_at).toLocaleString() : ""}` : "No"} testid="user-detail-field-free-access" />
          <DetailRow label="Promo code" value={u.signup_promo_code || "—"} testid="user-detail-field-promo-code" />
          <DetailRow label="Last password reset" value={resetSummary(u.last_password_reset)} testid="user-detail-field-last-reset" />
          <DetailRow label="Joined" value={u.created_at ? new Date(u.created_at).toLocaleString() : ""} testid="user-detail-field-joined" />
          <DetailRow label="Terms version" value={u.terms_version
            ? `${u.terms_version} · accepted ${u.terms_accepted_at ? new Date(u.terms_accepted_at).toLocaleString() : "—"}`
            : "Not recorded (pre-consent user)"} testid="user-detail-field-terms" />
          <DetailRow label="Privacy version" value={u.privacy_version
            ? `${u.privacy_version} · accepted ${u.privacy_accepted_at ? new Date(u.privacy_accepted_at).toLocaleString() : "—"}`
            : "Not recorded (pre-consent user)"} testid="user-detail-field-privacy" />
        </div>
      </section>

      {(u.photo_path || u.pending_photo_path) && (
        <section data-testid="user-detail-photos-section">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Profile photo</h4>
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4 flex flex-wrap gap-4">
            {u.photo_path && (
              <div data-testid="user-detail-photo-approved">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Live</p>
                <img src={fileHref(u.photo_path)} alt="Approved profile photo" className="w-28 h-28 rounded-xl object-cover border border-sage/20" />
              </div>
            )}
            {u.pending_photo_path && (
              <div data-testid="user-detail-photo-pending">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Awaiting review</p>
                <img src={fileHref(u.pending_photo_path)} alt="Pending profile photo" className="w-28 h-28 rounded-xl object-cover border border-amber-300" />
              </div>
            )}
            {u.photo_rejection_reason && (
              <p className="w-full text-xs text-terracotta-dark bg-terracotta-light/60 rounded-xl p-2">
                <strong>Last rejection:</strong> {u.photo_rejection_reason}
              </p>
            )}
          </div>
        </section>
      )}

      {pp && (
        <section data-testid="user-detail-parent-profile">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Family profile (Parent)</h4>
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4">
            <DetailRow label="Children" value={(pp.children || []).map((c) => `${c.age || "?"} yrs${c.gender ? ` · ${c.gender}` : ""}`).join(", ")} testid="user-detail-parent-children" />
            <DetailRow label="Interests" value={(pp.interests || []).join(", ")} testid="user-detail-parent-interests" />
            <DetailRow label="About family" value={pp.about_family} testid="user-detail-parent-about" />
          </div>
        </section>
      )}

      {u.role === "parent" && (partner || linkedFrom) && (
        <section data-testid="user-detail-partner-section">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Linked second parent</h4>
          {partner && (
            <div className="bg-white/80 border border-sage/20 rounded-2xl p-4 flex items-center gap-4" data-testid="user-detail-partner-forward">
              <img
                src={(partner.photo_status === "approved" && partner.photo_path) ? fileHref(partner.photo_path) : "/logo.png"}
                alt={partner.name || "Second parent"}
                className="w-14 h-14 rounded-full object-cover border border-sage/30 bg-white shrink-0"
                data-testid="user-detail-partner-photo"
              />
              <div className="min-w-0 flex-1">
                <p className="font-bold text-forest truncate" data-testid="user-detail-partner-name">{partner.name || partner.email}</p>
                <p className="text-xs text-forest/60 truncate" data-testid="user-detail-partner-email">{partner.email}</p>
                <p className="text-[10px] text-forest/50 mt-0.5">Subscription: <strong className="uppercase">{partner.subscription_status}</strong></p>
              </div>
            </div>
          )}
          {linkedFrom && (
            <div className="mt-2 bg-white/80 border border-sage/20 rounded-2xl p-4 flex items-center gap-4" data-testid="user-detail-partner-back">
              <img
                src={(linkedFrom.photo_status === "approved" && linkedFrom.photo_path) ? fileHref(linkedFrom.photo_path) : "/logo.png"}
                alt={linkedFrom.name || "Primary parent"}
                className="w-14 h-14 rounded-full object-cover border border-sage/30 bg-white shrink-0"
              />
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50">Linked by primary parent</p>
                <p className="font-bold text-forest truncate" data-testid="user-detail-linked-from-name">{linkedFrom.name || linkedFrom.email}</p>
                <p className="text-xs text-forest/60 truncate" data-testid="user-detail-linked-from-email">{linkedFrom.email}</p>
              </div>
            </div>
          )}
        </section>
      )}

      {vp && (
        <section data-testid="user-detail-vendor-profile">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Vendor profile</h4>
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4 space-y-3">
            {vp.logo_path && (
              <div data-testid="user-detail-vendor-logo-wrap">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Logo</p>
                <img src={fileHref(vp.logo_path)} alt="Vendor logo" className="w-24 h-24 rounded-2xl object-cover border border-sage/20" data-testid="user-detail-vendor-logo" />
              </div>
            )}
            <DetailRow label="Business name" value={vp.business_name} testid="user-detail-vendor-business-name" />
            <DetailRow label="Description" value={vp.description} testid="user-detail-vendor-description" />
            <DetailRow label="Categories" value={(vp.categories || []).join(", ")} testid="user-detail-vendor-categories" />
            <DetailRow label="Location" value={vp.location} testid="user-detail-vendor-location" />
            <DetailRow label="Phone" value={vp.phone} testid="user-detail-vendor-phone" />
            <DetailRow label="Email" value={vp.email || vp.contact_email} testid="user-detail-vendor-email" />
            <DetailRow label="Website" value={vp.website} testid="user-detail-vendor-website" />
            <DetailRow label="Social media" value={vp.social_media} testid="user-detail-vendor-social" />
            <DetailRow label="Services" value={(vp.services || []).map((s) => s.name || s.title).filter(Boolean).join(", ")} testid="user-detail-vendor-services" />
            <AdminBranchEditor userId={u.user_id} vp={vp} setDetailData={setDetailData} />
            {(vp.image_paths || []).length > 0 && (
              <div data-testid="user-detail-vendor-gallery">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Product photos ({vp.image_paths.length})</p>
                <div className="flex flex-wrap gap-2">
                  {vp.image_paths.map((p, i) => (
                    <a key={i} href={fileHref(p)} target="_blank" rel="noopener noreferrer">
                      <img src={fileHref(p)} alt={`Vendor photo ${i + 1}`} className="w-24 h-24 rounded-xl object-cover border border-sage/20" data-testid={`user-detail-vendor-image-${i}`} />
                    </a>
                  ))}
                </div>
              </div>
            )}
            {(vp.flyer_paths || []).length > 0 && (
              <div data-testid="user-detail-vendor-flyers">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Flyers / documents ({vp.flyer_paths.length})</p>
                <ul className="space-y-1 text-sm">
                  {vp.flyer_paths.map((f, i) => (
                    <li key={i}>
                      <a className="text-sage-dark underline hover:text-sage" href={fileHref(f.path || f)} target="_blank" rel="noopener noreferrer" data-testid={`user-detail-vendor-flyer-${i}`}>
                        {f.name || f.path || `Flyer ${i + 1}`}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {rp && (
        <section data-testid="user-detail-role-profile">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">
            {u.role === "event_creator" ? "Event creator profile" : u.role === "marketplace_user" ? "Marketplace profile" : "Role profile"}
          </h4>
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4 space-y-3">
            {(rp.business_logo || rp.company_logo) && (
              <div data-testid="user-detail-role-logo-wrap">
                <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">
                  {u.role === "marketplace_user" ? "Business logo" : "Company logo"}
                </p>
                <img src={fileHref(rp.business_logo || rp.company_logo)} alt="Business logo" className="w-24 h-24 rounded-2xl object-cover border border-sage/20" data-testid="user-detail-role-logo" />
              </div>
            )}
            <DetailRow label="Business name" value={rp.business_name} testid="user-detail-role-business-name" />
            <DetailRow label="Organizer name" value={rp.organizer_name} testid="user-detail-role-organizer" />
            <DetailRow label="Location" value={rp.location} testid="user-detail-role-location" />
            <DetailRow label="Phone" value={rp.phone || rp.contact_number} testid="user-detail-role-phone" />
            <DetailRow label="Email" value={rp.email || rp.contact_email} testid="user-detail-role-email" />
            <DetailRow label="Website" value={rp.website} testid="user-detail-role-website" />
            <DetailRow label="Social" value={rp.social_links} testid="user-detail-role-social" />
            {rp.description && <DetailRow label="Description" value={rp.description} testid="user-detail-role-description" />}
            {u.role === "marketplace_user" && (
              <>
                {(rp.product_categories || []).length > 0 && (
                  <div data-testid="user-detail-role-product-categories">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Product categories</p>
                    <div className="flex flex-wrap gap-1.5">
                      {rp.product_categories.map((c) => (
                        <span key={c} className="text-xs font-semibold text-sage-dark bg-sage-light/70 border border-sage/30 rounded-full px-2.5 py-1">{c}</span>
                      ))}
                    </div>
                  </div>
                )}
                {(() => {
                  const slots = (rp.product_slots || []).filter((s) => s && (s.photo || s.description || s.price));
                  if (slots.length === 0) return null;
                  return (
                    <div data-testid="user-detail-role-product-slots">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-forest/50 mb-1">Products ({slots.length})</p>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {slots.map((s, i) => (
                          <div key={i} className="bg-white/80 border border-sage/20 rounded-2xl p-3 flex gap-3" data-testid={`user-detail-role-product-slot-${i}`}>
                            {s.photo ? (
                              <img src={fileHref(s.photo)} alt={`Product ${i + 1}`} className="w-20 h-20 rounded-xl object-cover border border-sage/20 shrink-0" data-testid={`user-detail-role-product-slot-photo-${i}`} />
                            ) : (
                              <div className="w-20 h-20 rounded-xl bg-softstone/80 border border-sage/20 shrink-0 flex items-center justify-center">
                                <BookOpen className="w-5 h-5 text-forest/40" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-[11px] font-bold uppercase tracking-wide text-forest/50">Product {i + 1}</p>
                              {s.description && <p className="text-xs text-forest/80 line-clamp-3 mt-0.5" data-testid={`user-detail-role-product-slot-desc-${i}`}>{s.description}</p>}
                              {s.price && <p className="text-sm font-bold text-terracotta-dark mt-1" data-testid={`user-detail-role-product-slot-price-${i}`}>R{s.price}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {rp.social_link_1 && <DetailRow label="Social link 1" value={rp.social_link_1} testid="user-detail-role-social-link-1" />}
                {rp.social_link_2 && <DetailRow label="Social link 2" value={rp.social_link_2} testid="user-detail-role-social-link-2" />}
                {rp.social_link_3 && <DetailRow label="Social link 3" value={rp.social_link_3} testid="user-detail-role-social-link-3" />}
              </>
            )}
          </div>
        </section>
      )}

      {eventsCreated.length > 0 && (
        <section data-testid="user-detail-events-section">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Events hosted ({eventsCreated.length})</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {eventsCreated.map((e) => {
              const photo = e.event_photo || e.image_url;
              return (
                <div key={e.event_id} className="bg-white/80 border border-sage/20 rounded-2xl p-3 flex gap-3" data-testid={`user-detail-event-${e.event_id}`}>
                  {photo ? (
                    <img src={fileHref(photo)} alt={e.event_name || e.title} className="w-16 h-16 rounded-xl object-cover border border-sage/20 shrink-0" data-testid={`user-detail-event-photo-${e.event_id}`} />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-softstone/80 border border-sage/20 shrink-0 flex items-center justify-center">
                      <CalendarDays className="w-5 h-5 text-forest/40" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-forest text-sm truncate">{e.event_name || e.title}</p>
                    <p className="text-[11px] text-forest/60">{e.event_date || e.date || "—"}</p>
                    <p className="text-[10px] text-forest/50 truncate">{e.location || ""}</p>
                    <span className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${e.status === "approved" ? "bg-sage-light text-sage-dark" : e.status === "rejected" ? "bg-terracotta-light text-terracotta-dark" : "bg-amber-100 text-amber-800"}`}>
                      {e.status || "approved"}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {listings.length > 0 && (
        <section data-testid="user-detail-listings-section">
          <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Marketplace listings ({listings.length})</h4>
          <div className="grid sm:grid-cols-2 gap-3">
            {listings.map((l) => {
              const cover = (l.photos || [])[0];
              return (
                <div key={l.listing_id} className="bg-white/80 border border-sage/20 rounded-2xl p-3 flex gap-3" data-testid={`user-detail-listing-${l.listing_id}`}>
                  {cover ? (
                    <img src={fileHref(cover)} alt={l.product_name} className="w-16 h-16 rounded-xl object-cover border border-sage/20 shrink-0" data-testid={`user-detail-listing-photo-${l.listing_id}`} />
                  ) : (
                    <div className="w-16 h-16 rounded-xl bg-softstone/80 border border-sage/20 shrink-0 flex items-center justify-center">
                      <BookOpen className="w-5 h-5 text-forest/40" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-forest text-sm truncate">{l.product_name}</p>
                    <p className="text-[11px] text-forest/60">{l.category}{l.price ? ` · ${l.price}` : ""}</p>
                    <span className={`inline-block mt-1 text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${l.status === "approved" ? "bg-sage-light text-sage-dark" : l.status === "rejected" ? "bg-terracotta-light text-terracotta-dark" : "bg-amber-100 text-amber-800"}`}>
                      {l.status || "approved"}
                    </span>
                    {(l.photos || []).length > 1 && (
                      <p className="text-[10px] text-forest/50 mt-0.5">+{l.photos.length - 1} more photo{l.photos.length - 1 === 1 ? "" : "s"}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section data-testid="user-detail-errors-section">
        <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2 flex items-center gap-1">
          <AlertTriangle className="w-3.5 h-3.5 text-terracotta-dark" /> Sign-up / registration errors
        </h4>
        {(!d.errors || d.errors.length === 0) ? (
          <div className="bg-white/60 border border-sage/20 rounded-2xl p-4 text-sm text-forest/50" data-testid="user-detail-no-errors">
            No errors recorded for this user.
          </div>
        ) : (
          <div className="space-y-2" data-testid="user-detail-errors-list">
            {d.errors.map((err, i) => (
              <div key={err.error_id || i} className="bg-terracotta-light/40 border border-terracotta/30 rounded-xl p-3 text-sm" data-testid={`user-detail-error-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta text-white px-2 py-0.5 rounded-full">{err.step || "unknown step"}</span>
                  {err.code && <span className="text-[10px] font-bold uppercase text-terracotta-dark">{err.code}</span>}
                  <span className="ml-auto text-[10px] text-forest/50">{err.at ? new Date(err.at).toLocaleString() : ""}</span>
                </div>
                <p className="text-forest/80">{err.message}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function AdminBranchEditor({ userId, vp, setDetailData }) {
  const [hasMultiple, setHasMultiple] = useState(!!vp.has_multiple_branches);
  const [branches, setBranches] = useState(() =>
    (vp.branches || []).map((b) => ({ ...EMPTY_BRANCH, ...b }))
  );
  const [saving, setSaving] = useState(false);

  // Keep the editor in sync when the admin opens a different vendor without
  // closing the dialog first (parent swaps `d` + `vp`).
  useEffect(() => {
    setHasMultiple(!!vp.has_multiple_branches);
    setBranches((vp.branches || []).map((b) => ({ ...EMPTY_BRANCH, ...b })));
  }, [vp.vendor_id, vp.has_multiple_branches, vp.branches]);

  const updateBranch = (i, patch) => {
    const arr = [...branches];
    arr[i] = { ...arr[i], ...patch };
    setBranches(arr);
  };
  const removeBranch = (i) => setBranches(branches.filter((_, j) => j !== i));
  const addBranch = () => setBranches([...branches, { ...EMPTY_BRANCH }]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        has_multiple_branches: hasMultiple && branches.length > 0,
        branches: hasMultiple ? branches : [],
      };
      const { data } = await api.patch(`/admin/vendors/${userId}/branches`, payload);
      setDetailData((prev) => prev ? { ...prev, vendor_profile: { ...prev.vendor_profile, ...data.profile } } : prev);
      toast.success("Branches updated");
    } catch (e) {
      toast.error(errMsg(e, "Could not save branches"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 pt-3 border-t border-sage/15" data-testid="user-detail-vendor-branches-editor">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-black uppercase tracking-wide text-sage-dark flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> Branches
        </p>
        <div className="flex gap-2">
          {[
            { v: false, label: "No" },
            { v: true, label: "Yes" },
          ].map((o) => (
            <button
              key={o.label}
              type="button"
              onClick={() => {
                setHasMultiple(o.v);
                if (o.v && branches.length === 0) setBranches([{ ...EMPTY_BRANCH }]);
              }}
              className={`text-[11px] font-bold px-3 py-1 rounded-full border transition-colors ${hasMultiple === o.v ? "bg-sage text-white border-sage" : "bg-white text-forest/70 border-sage/30 hover:border-sage"}`}
              data-testid={`admin-branch-toggle-${o.label.toLowerCase()}`}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {hasMultiple && (
        <div className="space-y-3" data-testid="admin-branches-list">
          {branches.map((b, i) => (
            <div key={i} className="bg-softstone/60 rounded-xl p-3 space-y-2" data-testid={`admin-branch-${i}`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-bold text-forest">Branch {i + 1}</span>
                <button
                  type="button"
                  onClick={() => removeBranch(i)}
                  className="p-1 text-terracotta-dark hover:bg-terracotta-light rounded-lg"
                  aria-label={`Remove branch ${i + 1}`}
                  data-testid={`admin-branch-remove-${i}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-forest/60">Branch name</Label>
                  <Input value={b.name} onChange={(e) => updateBranch(i, { name: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30 h-9 text-sm" data-testid={`admin-branch-name-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-forest/60">Address</Label>
                  <Input value={b.address} onChange={(e) => updateBranch(i, { address: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30 h-9 text-sm" data-testid={`admin-branch-address-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-forest/60">City</Label>
                  <Input value={b.city} onChange={(e) => updateBranch(i, { city: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30 h-9 text-sm" data-testid={`admin-branch-city-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-forest/60">Province</Label>
                  <Input value={b.province} onChange={(e) => updateBranch(i, { province: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30 h-9 text-sm" data-testid={`admin-branch-province-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-forest/60">Phone</Label>
                  <Input value={b.phone} onChange={(e) => updateBranch(i, { phone: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30 h-9 text-sm" data-testid={`admin-branch-phone-${i}`} />
                </div>
                <div>
                  <Label className="text-[10px] text-forest/60">Email</Label>
                  <Input type="email" value={b.email} onChange={(e) => updateBranch(i, { email: e.target.value })} className="mt-1 rounded-xl bg-white border-sage/30 h-9 text-sm" data-testid={`admin-branch-email-${i}`} />
                </div>
              </div>
            </div>
          ))}
          <Button type="button" size="sm" variant="outline" onClick={addBranch} className="rounded-full" data-testid="admin-branch-add">
            <Plus className="w-3.5 h-3.5 mr-1" /> Add branch
          </Button>
        </div>
      )}

      <div className="mt-3 flex justify-end">
        <Button type="button" size="sm" disabled={saving} onClick={save} className="rounded-full bg-sage hover:bg-sage-dark text-white" data-testid="admin-branches-save">
          <Save className="w-3.5 h-3.5 mr-1" /> {saving ? "Saving..." : "Save branches"}
        </Button>
      </div>
    </div>
  );
}
